/**
 * use-users.ts
 *
 * Hooks for the admin Users tab in Settings:
 *   - useOrgUsers()                       — list users in the current org
 *   - useUpdateUserNotificationPrefs()    — admin: persist a user's notification toggles
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface OrgUser {
  id: string;
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatarUrl?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  notifyOnWorkOrderCreated: boolean;
  // Per-user: notify when another user uploads a document to a Load.
  loadsDocumentUploadNotification: boolean;
  // #1712 — per-user opt-in for the daily Driver / Equipment expiry alerts.
  notifyDriverExpiries: boolean;
  notifyMissingGpsEldLinks: boolean;
  notifyEquipmentExpiries: boolean;
  // #1827 — per-user opt-in for the daily Paycom shift-rejection email.
  notifyPaycomShiftRejections: boolean;
  // ATL-000989 — per-user opt-in for the EDI-cancellation email.
  notifyEdiCancellation: boolean;
  // ATL-000989 — per-user opt-in for My Carrier Packet carrier-change
  // notifications.
  notifyMcpCarrier: boolean;
  // ATL-000989 — per-user opt-in for the "driver running with location
  // service off" email.
  notifyDriverLocationServiceOff: boolean;
  // ATL-001042 — per-user opt-in for the Blacklist Carriers rating-warning email.
  notifyBlacklistRatingWarning: boolean;
  // ATL-000440 — "Allow user to add manual date/timestamps" (V1
  // users.update_geofence_date_stamp). Gates the USPS completion
  // geofence-dates check server-side.
  updateGeofenceDateStamp: boolean;
  // ATL-000610 — "Allow user to update Geofence date stamp" (V1
  // users.permission_geofence_timestamp). Gates the Fix Timestamps button.
  permissionGeofenceTimestamp: boolean;
  // #1304 — Terminal assignments driving the non-admin load-visibility gate.
  terminalIds?: string[];
  // ATL-000990 — Native App login phone (canonical 10-digit national number,
  // normalized server-side). Shown + editable in the Settings → Users drawer.
  phoneNumber?: string | null;
  // Commission module — per-user default commission percentages.
  commissionPercent?: string | null;
  creatorCommissionPercent?: string | null;
  dispatcherCommissionPercent?: string | null;
}

interface OrgUsersResponse {
  data: OrgUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface OrgUsersFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  sortBy?: 'createdAt' | 'firstName' | 'lastName' | 'email' | 'role';
  sortOrder?: 'asc' | 'desc';
  /** When true, return only soft-deleted users (Show Deleted toggle). */
  showDeleted?: boolean;
}

/**
 * Lightweight user list for load-form dropdowns (e.g. Commission Dispatcher).
 * Calls /users/for-load-dropdown — gated on loads.view so all operational
 * roles (dispatcher, manager, sales, etc.) can access it. Matches V1's
 * /api/dropdown/users behaviour: all active org users, no role filter.
 */
export function useUsersForLoadDropdown() {
  return useQuery({
    queryKey: ['users-for-load-dropdown'],
    queryFn: async () => {
      const res = await api.get<{ data: Pick<OrgUser, 'id' | 'firstName' | 'lastName' | 'email'>[] }>(
        '/users/for-load-dropdown',
      );
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Users for the Commission Report "Users" filter dropdown (V1 parity).
 * Calls /commissions/report-users — internal staff (salesperson / creator /
 * dispatcher candidates), excluding driver / carrier / customer roles. Gated by
 * the same auth + enable_commission_module check as the report itself.
 */
export function useCommissionFilterUsers() {
  return useQuery({
    queryKey: ['commission-filter-users'],
    queryFn: async () => {
      const res = await api.get<{ data: Pick<OrgUser, 'id' | 'firstName' | 'lastName' | 'email'>[] }>(
        '/commissions/report-users',
      );
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrgUsers(filters: OrgUsersFilters = {}) {
  const {
    page = 1,
    limit = 100,
    search,
    role,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    showDeleted = false,
  } = filters;

  return useQuery({
    queryKey: ['org-users', { page, limit, search, role, sortBy, sortOrder, showDeleted }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);
      if (role) params.set('role', role);
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (showDeleted) params.set('showDeleted', 'true');
      const res = await api.get<OrgUsersResponse>(`/users?${params}`);
      return res.data;
    },
  });
}

/**
 * Settings → Create User. Creates a new Super Admin under the caller's active
 * organization. Role + permissions are decided server-side (always
 * super_admin); the client only sends identity + credential fields. Invalidates
 * the org-users list so the new account appears immediately.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    }) => {
      const res = await api.post<{ data: OrgUser }>('/users', input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.delete<{ data: OrgUser }>(`/users/${userId}`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useUpdateUserCommissionRates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      commissionPercent: number | null;
      creatorCommissionPercent: number | null;
      dispatcherCommissionPercent: number | null;
    }) => {
      const { userId, ...body } = input;
      const res = await api.patch<{ data: OrgUser }>(`/users/${userId}/commission-rates`, body);
      return res.data.data;
    },
    onSuccess: (updated) => {
      // Immediately patch all matching org-users cache entries so the Settings
      // drawer shows saved values even before the background refetch completes.
      queryClient.setQueriesData<OrgUsersResponse>(
        { queryKey: ['org-users'], exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((u) =>
              u.id === updated.id
                ? {
                    ...u,
                    commissionPercent:           updated.commissionPercent,
                    creatorCommissionPercent:    updated.creatorCommissionPercent,
                    dispatcherCommissionPercent: updated.dispatcherCommissionPercent,
                  }
                : u,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

/**
 * ATL-000990 — admin: set/clear a user's Native App login phone. `null`
 * clears the phone. The server normalizes + enforces platform-global
 * uniqueness through the shared login-phone helpers. Patches the matching
 * org-users cache entry immediately so the drawer round-trips the saved
 * value, then invalidates for a background refetch.
 */
export function useUpdateUserPhone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      phoneNumber,
    }: {
      userId: string;
      phoneNumber: string | null;
    }) => {
      const res = await api.patch<{ data: { id: string; phoneNumber: string | null } }>(
        `/users/${userId}/phone`,
        { phoneNumber },
      );
      return res.data.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueriesData<OrgUsersResponse>(
        { queryKey: ['org-users'], exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((u) =>
              u.id === updated.id ? { ...u, phoneNumber: updated.phoneNumber } : u,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

/**
 * Admin: update a user's name from the Settings → Users Edit drawer. The
 * drawer's single "Full Name" input is split by the caller into first + last
 * (lastName may be '' for single-word names). Patches the matching org-users
 * cache entry immediately so the grid shows the new name without waiting for
 * the background refetch, then invalidates for a fresh read.
 */
export function useUpdateUserName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      firstName,
      lastName,
    }: {
      userId: string;
      firstName: string;
      lastName: string;
    }) => {
      const res = await api.patch<{ data: { id: string; firstName: string; lastName: string } }>(
        `/users/${userId}/name`,
        { firstName, lastName },
      );
      return res.data.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueriesData<OrgUsersResponse>(
        { queryKey: ['org-users'], exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((u) =>
              u.id === updated.id
                ? { ...u, firstName: updated.firstName, lastName: updated.lastName }
                : u,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

/**
 * ATL-001022 — admin: change a user's role (e.g. Manager → Admin) from
 * Settings → General Settings. Calls the existing `PATCH /users/:id/role`
 * endpoint (admin + `users.manage` gated server-side). Patches the matching
 * org-users cache entry immediately so the grid reflects the new role without
 * waiting for the background refetch, then invalidates for a fresh read.
 *
 * The server rejects changing your own role ("You cannot change your own
 * role"); the caller surfaces that error message to the user.
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const res = await api.patch<{ data: OrgUser }>(`/users/${userId}/role`, { role });
      return res.data.data;
    },
    onSuccess: (updated) => {
      queryClient.setQueriesData<OrgUsersResponse>(
        { queryKey: ['org-users'], exact: false },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((u) =>
              u.id === updated.id ? { ...u, role: updated.role } : u,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useRestoreUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post<{ data: OrgUser }>(`/users/${userId}/restore`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

export function useUpdateUserNotificationPrefs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      notifyOnWorkOrderCreated,
      loadsDocumentUploadNotification,
      notifyDriverExpiries,
      notifyEquipmentExpiries,
      notifyMissingGpsEldLinks,
      notifyPaycomShiftRejections,
      notifyEdiCancellation,
      notifyMcpCarrier,
      notifyDriverLocationServiceOff,
      notifyBlacklistRatingWarning,
      updateGeofenceDateStamp,
      permissionGeofenceTimestamp,
    }: {
      userId: string;
      notifyOnWorkOrderCreated?: boolean;
      loadsDocumentUploadNotification?: boolean;
      notifyDriverExpiries?: boolean;
      notifyEquipmentExpiries?: boolean;
      /** Daily missing-GPS/ELD-links report opt-in. */
      notifyMissingGpsEldLinks?: boolean;
      // #1827 — per-user opt-in for the daily Paycom shift-rejection email.
      notifyPaycomShiftRejections?: boolean;
      // ATL-000989 — per-user opt-in for the EDI-cancellation email.
      notifyEdiCancellation?: boolean;
      // ATL-000989 — per-user opt-in for My Carrier Packet carrier-change
      // notifications.
      notifyMcpCarrier?: boolean;
      // ATL-000989 — per-user opt-in for the "driver running with location
      // service off" email.
      notifyDriverLocationServiceOff?: boolean;
      /** ATL-001042 — Blacklist Carriers rating-warning email opt-in. */
      notifyBlacklistRatingWarning?: boolean;
      // ATL-000440 — "Allow user to add manual date/timestamps" (V1
      // users.update_geofence_date_stamp). Gates the USPS completion
      // geofence-dates check server-side.
      updateGeofenceDateStamp?: boolean;
      // ATL-000610 — gates the Fix Timestamps GPS backfill button.
      permissionGeofenceTimestamp?: boolean;
    }) => {
      const res = await api.patch<{
        data: {
          id: string;
          notifyOnWorkOrderCreated: boolean;
          loadsDocumentUploadNotification: boolean;
          notifyDriverExpiries: boolean;
          notifyEquipmentExpiries: boolean;
          notifyMissingGpsEldLinks: boolean;
          notifyPaycomShiftRejections: boolean;
          notifyEdiCancellation: boolean;
          notifyMcpCarrier: boolean;
          notifyDriverLocationServiceOff: boolean;
          notifyBlacklistRatingWarning: boolean;
          updateGeofenceDateStamp: boolean;
          permissionGeofenceTimestamp: boolean;
        };
      }>(
        `/users/${userId}/notification-prefs`,
        {
          notifyOnWorkOrderCreated,
          loadsDocumentUploadNotification,
          notifyDriverExpiries,
          notifyEquipmentExpiries,
          notifyMissingGpsEldLinks,
          notifyPaycomShiftRejections,
          notifyEdiCancellation,
          notifyMcpCarrier,
          notifyDriverLocationServiceOff,
          notifyBlacklistRatingWarning,
          updateGeofenceDateStamp,
          permissionGeofenceTimestamp,
        },
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

/**
 * #1304 — admin: replace a user's Terminal assignments. Persists to the
 * `user_terminals` junction the load-visibility gate reads from. Sends the
 * full selection (replace semantics). Invalidates the user list so the Edit
 * drawer round-trips the saved set.
 */
export function useAssignUserTerminals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      terminalIds,
    }: {
      userId: string;
      terminalIds: string[];
    }) => {
      const res = await api.patch<{ data: { terminalIds: string[] } }>(
        `/users/${userId}/terminals`,
        { terminalIds },
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// WAYP-INVITE: invitations
// ─────────────────────────────────────────────────────────────────────────────

export interface UserInvitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  invitedBy: { id: string; firstName: string; lastName: string; email: string } | null;
}

export function useUserInvitations(opts: { includeHistory?: boolean } = {}) {
  return useQuery({
    queryKey: ['user-invitations', opts.includeHistory ?? false] as const,
    queryFn: async () => {
      const qs = opts.includeHistory ? '?includeHistory=true' : '';
      const res = await api.get<{ data: UserInvitation[] }>(`/users/invitations${qs}`);
      return res.data.data;
    },
  });
}

export function useGrantableRoles() {
  return useQuery({
    queryKey: ['user-invitations', 'grantable-roles'] as const,
    queryFn: async () => {
      const res = await api.get<{ data: string[] }>('/users/invitations/grantable-roles');
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 min — role list rarely changes mid-session
  });
}

export function useCreateInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; role: string }) => {
      const res = await api.post<{ data: UserInvitation }>('/users/invitations', input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-invitations'] });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const res = await api.post<{ data: UserInvitation }>(
        `/users/invitations/${invitationId}/resend`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-invitations'] });
    },
  });
}

export function useCancelInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const res = await api.post<{ data: { id: string; status: string } }>(
        `/users/invitations/${invitationId}/cancel`,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-invitations'] });
    },
  });
}
